/**
 * Evaluates a simple amount expression (calculator-style).
 * Supports +, -, *, /, parentheses, spaces, and French decimal commas.
 * Examples: "10+5", "10 + 5", "10,5*2", "(20-5)/3"
 * Results are always rounded to 2 decimal places.
 */

export function roundAmount(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number(numeric.toFixed(2));
}

export function evaluateAmountExpression(input) {
  if (input === null || input === undefined) {
    return { ok: false, error: "Montant à saisir" };
  }

  const raw = String(input).trim();
  if (!raw) {
    return { ok: false, error: "Montant à saisir" };
  }

  const expression = raw.replace(/\s+/g, "").replace(/,/g, ".");

  if (!/^[-+*/().\d]+$/.test(expression)) {
    return { ok: false, error: "Montant invalide" };
  }

  if (/[*/+-]{2,}/.test(expression.replace(/^\+|^-/, ""))) {
    // allow leading +/- but reject other consecutive operators like 10++5
    const withoutUnary = expression.replace(/(^|[(*/])([+-])/g, "$1");
    if (/[*/+-]{2,}/.test(withoutUnary)) {
      return { ok: false, error: "Montant invalide" };
    }
  }

  try {
    // Expression is character-whitelisted above; Function is used only for arithmetic.
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${expression});`)();

    if (typeof result !== "number" || !Number.isFinite(result)) {
      return { ok: false, error: "Montant invalide" };
    }

    return { ok: true, value: roundAmount(result) };
  } catch (error) {
    return { ok: false, error: "Montant invalide" };
  }
}

export function resolveAmountValue(input) {
  const evaluated = evaluateAmountExpression(input);
  if (!evaluated.ok) {
    return null;
  }
  return evaluated.value;
}

export function formatResolvedAmount(value) {
  const rounded = roundAmount(value);
  if (rounded === null) {
    return "";
  }
  // Always show exactly 2 decimals (5 → "5.00", -165.5 → "-165.50").
  return rounded.toFixed(2);
}
